import { Link } from 'react-router-dom';
import { courseCards } from '@/mocks/coursesData';
import { useTranslation } from 'react-i18next';

const cardImages = [
  '/images/generated/course-compare-essential.jpg',
  '/images/generated/course-compare-advanced.jpg',
  '/images/generated/course-compare-master.jpg',
];

export default function CourseCards() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-12">
          {String(t('courses.cards.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('courses.cards.title')}
          </h2>
          {String(t('courses.cards.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('courses.cards.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {courseCards.map((course, idx) => (
            <div
              key={idx}
              className={`card-glow group bg-background-100/60 border overflow-hidden cursor-pointer flex flex-col ${
                course.recommended
                  ? 'border-accent-500/40'
                  : 'border-background-200/60'
              }`}
            >
              {course.recommended && (
                <div className="bg-accent-500 text-primary-950 text-[12px] tracking-[0.1em] font-medium text-center py-1.5">
                  {t('courses.cards.recommended')}
                </div>
              )}
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <img
                  src={cardImages[idx]}
                  alt={`${t(`courses.cards.${idx}.name`)}${t('courses.cards.viewDetail')}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-heading text-[17px] text-foreground-900 tracking-[0.05em] mb-1 group-hover:text-accent-600 transition-colors">
                  {t(`courses.cards.${idx}.name`)}
                </h3>
                <p className="text-[12px] text-accent-500 tracking-[0.12em] mb-4">
                  {course.nameEn}
                </p>

                {idx > 0 && (
                  <div className="mb-4 px-3 py-2.5 bg-accent-500/10 border-l-2 border-accent-500">
                    <p className="text-[12px] text-accent-700 font-medium tracking-[0.02em] mb-1 flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line"></i>
                      {t(`courses.cards.${idx}.inclusionBadge`)}
                    </p>
                    <p className="text-[11px] text-foreground-600 tracking-[0.02em] leading-relaxed">
                      {t(`courses.cards.${idx}.progressionShort`)}
                    </p>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13px] text-foreground-500 tracking-[0.04em]">{t('courses.cards.durationLabel')}</span>
                    <span className="text-[14px] text-foreground-800 tracking-[0.04em]">{t(`courses.cards.${idx}.duration`)}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[13px] text-foreground-500 tracking-[0.04em]">{t('courses.cards.hoursLabel')}</span>
                    <span className="text-[15px] text-foreground-800 tracking-[0.04em] font-medium">{t(`courses.cards.${idx}.hours`)}</span>
                  </div>
                  <div className="flex items-baseline justify-between pt-2 border-t border-background-200/60">
                    <span className="text-[13px] text-foreground-500 tracking-[0.04em]">{t('courses.cards.priceLabel')}</span>
                    <span className="text-[18px] text-accent-600 tracking-[0.04em] font-medium">
                      ¥{Number(String(course.price).replace(/[^\d.]/g, '')).toLocaleString()}<span className="text-[12px]">{t('courses.cards.priceTax')}</span>
                    </span>
                  </div>
                </div>

                <div className="mb-3 pb-3 border-b border-background-200/60">
                  {String(t('courses.cards.targetLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-400 tracking-[0.06em] mb-1">{__s}</p>))}
                  <p className="text-[13px] text-foreground-700 tracking-[0.04em]">{t(`courses.cards.${idx}.target`)}</p>
                </div>

                <p className="text-[13px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-4 flex-1">
                  {t(`courses.cards.${idx}.description`)}
                </p>

                <div className="mb-4 p-3.5 bg-accent-500/[0.06] border-l-2 border-accent-500/40">
                  {String(t('courses.cards.goalLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-accent-600 tracking-[0.06em] mb-1.5 font-medium">{__s}</p>))}
                  <p className="text-[13px] text-foreground-700 tracking-[0.03em] leading-relaxed">
                    {t(`courses.row.${['essential', 'advanced', 'master'][idx]}.goal`)}
                  </p>
                </div>

                <div className="mb-4">
                  {String(t('courses.cards.certLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-400 tracking-[0.06em] mb-1">{__s}</p>))}
                  <p className="text-[13px] text-accent-600 tracking-[0.04em]">{t(`courses.cards.${idx}.certification`)}</p>
                </div>

                <Link
                  to={course.href}
                  className={`btn-glow inline-block w-full text-center py-3 text-[14px] tracking-[0.08em] font-medium transition-all duration-300 whitespace-nowrap ${
                    course.recommended
                      ? 'bg-accent-500 hover:bg-accent-400 text-primary-900'
                      : 'border border-foreground-300/30 text-foreground-700 hover:border-accent-400/50 hover:text-accent-600'
                  }`}
                >
                  {t('courses.cards.viewDetail')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}